/**
 * Conversiones canónicas para el BO.
 *
 *  - "nano USD" en este proyecto = bigint con escala 10^8 (1 USD = 100_000_000).
 *    Es jerga interna heredada de `packages/billing/src/money.ts` (SCALE);
 *    NO es nano SI (10^-9). Mantener este factor sincronizado con `Money.SCALE`.
 *  - duración: ms → "1.2s" / "350ms".
 *  - bigint: input puede venir como bigint, string o number. Strings con `-`
 *    se soportan. Falla suave devolviendo "$0.00" / "0 ms" si el input es
 *    inválido — el BO nunca debe romper render por un campo numérico raro.
 */

export type NanoLike = bigint | string | number | null | undefined;

/**
 * MUST stay in sync with `Money.SCALE` (`packages/billing/src/money.ts:14`).
 * El bug histórico era usar 10^9 acá — divisor 10x mayor → costos se veían
 * 10x más chicos en el admin que en la app (cliente usa Money.format
 * directamente desde billing, así que ahí siempre fue correcto).
 */
const NANO_PER_USD = 100_000_000n;

function toBigInt(value: NanoLike): bigint | null {
  if (value === null || value === undefined) return null;
  try {
    if (typeof value === 'bigint') return value;
    if (typeof value === 'number') {
      if (!Number.isFinite(value)) return null;
      return BigInt(Math.trunc(value));
    }
    const trimmed = value.trim();
    if (trimmed === '') return null;
    return BigInt(trimmed);
  } catch {
    return null;
  }
}

export function nanoToUsd(value: NanoLike, fractionDigits = 4): string {
  const n = toBigInt(value);
  if (n === null) return '$0.0000';
  const negative = n < 0n;
  const abs = negative ? -n : n;
  const whole = abs / NANO_PER_USD;
  const frac = abs % NANO_PER_USD;
  // Padding a 8 dígitos (SCALE = 10^8). Antes era 9 — el divisor estaba mal
  // y el pad también: alineados en 10^9 daban resultados consistentes
  // entre sí, pero ambos eran ×10 más chicos que el cliente.
  const fracStr = frac.toString().padStart(8, '0').slice(0, fractionDigits);
  const sign = negative ? '-' : '';
  return `${sign}$${whole.toString()}.${fracStr}`;
}

export function nanoToUsdCompact(value: NanoLike): string {
  const n = toBigInt(value);
  if (n === null) return '$0';
  const negative = n < 0n;
  const abs = negative ? -n : n;
  if (abs === 0n) return '$0';
  // Sub-cent: precisión adaptativa para que costos chicos (PA/STT/TTS por
  // turn ≈ $0.00001-$0.001) no aparezcan todos como '$0.0000'.
  //   abs <  $0.001  → 5 decimales (ej. $0.00009)
  //   $0.001 ≤ abs < $1  → 4 decimales (ej. $0.0015)
  //   abs ≥ $1       → 2 decimales (ej. $1.23)
  const ONE_MILLI_USD = NANO_PER_USD / 1000n;
  if (abs < ONE_MILLI_USD) {
    return `${negative ? '-' : ''}${nanoToUsd(abs, 5)}`;
  }
  if (abs < NANO_PER_USD) {
    return `${negative ? '-' : ''}${nanoToUsd(abs, 4)}`;
  }
  const whole = Number(abs / NANO_PER_USD);
  // Frac: dividimos por (SCALE / 10^4) = 10^4 para quedar con 4 dígitos
  // significativos antes de pasar a number (max safe int).
  const frac = Number((abs % NANO_PER_USD) / (NANO_PER_USD / 10_000n)) / 10_000;
  const total = whole + frac;
  return `${negative ? '-' : ''}$${total.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
}

export function formatDurationMs(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return '—';
  if (ms < 1) return '<1ms';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(s < 10 ? 2 : 1)}s`;
  const minutes = Math.floor(s / 60);
  const remSec = Math.round(s - minutes * 60);
  return `${minutes}m ${remSec}s`;
}

export function formatNumber(n: number | null | undefined, digits = 0): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  return n.toLocaleString('en-US', { maximumFractionDigits: digits, minimumFractionDigits: digits });
}

export function formatPercent(ratio: number | null | undefined, digits = 1): string {
  if (ratio === null || ratio === undefined || !Number.isFinite(ratio)) return '—';
  return `${(ratio * 100).toFixed(digits)}%`;
}

export function relativeTime(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '—';
  const diff = t - now.getTime();
  const abs = Math.abs(diff);
  const seconds = Math.round(abs / 1000);
  const minutes = Math.round(seconds / 60);
  const hours = Math.round(minutes / 60);
  const days = Math.round(hours / 24);
  const sign = diff <= 0 ? '' : 'in ';
  const suffix = diff <= 0 ? ' ago' : '';
  if (seconds < 60) return `${sign}${seconds}s${suffix}`;
  if (minutes < 60) return `${sign}${minutes}m${suffix}`;
  if (hours < 24) return `${sign}${hours}h${suffix}`;
  return `${sign}${days}d${suffix}`;
}

/**
 * Formatter reutilizable (instanciarlo por celda es caro en tablas largas).
 * Sin `timeZone` → usa la zona horaria local del operador, que es lo que se
 * quiere en el BO: "¿cuándo pasó esto en mi hora?".
 */
const DATE_TIME_FMT = new Intl.DateTimeFormat('es-AR', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/**
 * Fecha/hora absoluta y legible en hora local: "12 jun 2026, 14:23".
 *
 * Es el formato preferido en el BO sobre `relativeTime` ("1d ago"): el
 * operador necesita pinpointear el instante exacto (auditar sesiones,
 * transacciones, errores), no sólo la recencia. Devuelve "—" para input
 * nulo/ inválido, igual que `relativeTime`, para que sea drop-in.
 */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '—';
  const parts = DATE_TIME_FMT.formatToParts(d);
  const get = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value ?? '';
  return `${get('day')} ${get('month')} ${get('year')}, ${get('hour')}:${get('minute')}`;
}

export function shortId(id: string | null | undefined, head = 8): string {
  if (!id) return '—';
  return id.length <= head ? id : `${id.slice(0, head)}…`;
}
